import React from "react";
import CaseStudy from "../case/CaseStudy";
import {
  Section,
  Figure,
  Split,
  Sticky,
  Stage,
  Statement,
  Specs,
  Beat,
  Grid,
} from "../case/blocks";
import { useStageParallax, PanGallery } from "../case/motion";
import "../case/case.scss";

import imgHero from "../../../images/portablehood/ph-hero.png";
import imgLifestyle1 from "../../../images/portablehood/ph-lifestyle1.png";
import imgExplodedBase from "../../../images/portablehood/ph-exploded-base.png";
import imgExplodedDuct from "../../../images/portablehood/ph-exploded-duct.png";
import imgProjSide from "../../../images/portablehood/ph-proj-side.png";
import imgCooktop from "../../../images/portablehood/ph-cooktop.png";
import imgFinal from "../../../images/portablehood/ph-final.png";
import imgMood2 from "../../../images/portablehood/ph-mood2.png";
import imgMood4 from "../../../images/portablehood/ph-mood4.png";
import imgColor1 from "../../../images/portablehood/ph-color1.png";
import imgColor2 from "../../../images/portablehood/ph-color2.png";
import imgColor3 from "../../../images/portablehood/ph-color3.png";
import imgColor4 from "../../../images/portablehood/ph-color4.png";
import imgFinP2 from "../../../images/portablehood/ph-fin-p2.png";
import imgMat1 from "../../../images/portablehood/ph-mat1.png";
import imgLifestyle2 from "../../../images/portablehood/ph-lifestyle2.png";
import imgLifestyle3 from "../../../images/portablehood/ph-lifestyle3.png";
import imgMood1 from "../../../images/portablehood/ph-mood1.png";
import imgMood3 from "../../../images/portablehood/ph-mood3.png";
import imgMood5 from "../../../images/portablehood/ph-mood5.png";
import imgMood6 from "../../../images/portablehood/ph-mood6.png";
import imgFinS1 from "../../../images/portablehood/ph-fin-s1.png";
import imgFinS2 from "../../../images/portablehood/ph-fin-s2.png";
import imgProjTop from "../../../images/portablehood/ph-proj-top.png";

const BASE_PARTS = [
  { label: "Base - Upper Shell", body: "ABS" },
  { label: "Wire Mesh", body: "Aluminium 3003" },
  { label: "Base - Medium Shell", body: "ABS" },
  { label: "Base - Button", body: "ABS" },
  { label: "Base - Lower Shell", body: "ABS" },
  { label: "Charge Pin & PCB", body: "" },
  { label: "Base - Feet", body: "Rubber" },
  { label: "HEPA Filters", body: "" },
];

const ABS_NOTES = [
  {
    label: "Good surface finish",
    body: "ABS is used in automotive interiors as it has a good surface finish and is resistant to scratching.",
  },
  {
    label: "UV resistant",
    body: "ABS is naturally UV resistant which results in greater longevity of outdoor toys. Pure ABS is naturally white and so is an excellent carrier for a wide range of pigments.",
  },
  {
    label: "Housings for electronic devices",
    body: "Electronic device housings are often made from ABS owing to their good UV resistance.",
  },
];

const PortableHood = () => {
  useStageParallax();

  return (
    <CaseStudy
      slug="/portablehood"
      title="Portable Hood"
      tagline="Masters Design Group Workshop"
      year="2024 - 2025"
      client={{ name: "Midea with Poli.Design" }}
      roles={[
        "Form factor & ergonomics",
        "CMF (colour, material & finish)",
        "Product visualisation",
      ]}
      hero={{
        src: imgHero,
        alt: "Portable extraction hood, final render",
      }}
      lead={
        <p>
          The aim of the project was to design a hood suitable for people that
          have small apartments with open kitchen and living rooms.
        </p>
      }
    >
      <Stage
        src={imgLifestyle3}
        alt="The hood standing in an open-plan apartment"
        title="Made for small apartments with open kitchens."
        height="100dvh"
      />

      <Statement>
        Design a hood suitable for people from 25 to 50 years old that have
        small apartments with open kitchens and living rooms.
      </Statement>

      <Section id="brief" title="Project Brief">
        <Split
          media={
            <img
              src={imgLifestyle1}
              alt="The portable hood carried into an open-plan apartment kitchen"
            />
          }
          align="center"
        >
          <p className="cs-lead" style={{ marginTop: 0 }}>
            The main feature is its compact design and portability, this hood
            seamlessly adapts to the diverse culinary needs of the user. It can
            be moved effortlessly around your house, ensuring effective
            purification through the HEPA filters in the base.
          </p>
        </Split>
      </Section>

      <Sticky
        media={<img src={imgProjSide} alt="Core and base dimensioned side views" />}
      >
        <Beat title="Core">
          <p>
            The portable hood has the portability as its main feature, for this
            reason the maximum measurements are as limited as possible while
            still providing a good performance.
          </p>
        </Beat>
        <Specs
          items={[
            { label: "150 mm", body: "Core width" },
            { label: "180 mm", body: "Core height" },
            { label: "210 mm", body: "Core with base" },
            { label: "90 mm", body: "Base depth" },
          ]}
          columns={2}
        />
        <Beat title="Base">
          <p>
            The base of the product is the only part that always remains visible
            in the house. For this reason the two main constraints were the
            aesthetic and the limited dimensions for not being too bulky.
          </p>
        </Beat>
      </Sticky>

      <Section id="projection" title="Projections">
        <Figure
          src={imgProjTop}
          alt="Top and side orthographic projections of the portable hood"
          narrow
        />
      </Section>

      <Section id="exploded" title="Exploded view">
        <Split
          media={
            <img
              src={imgExplodedBase}
              alt="Exploded view of the portable hood base assembly"
            />
          }
          align="center"
        >
          <Specs items={BASE_PARTS} columns={1} />
        </Split>
      </Section>

      <Section id="cooktop" title="Cooktop & Duct">
        <Split
          media={
            <img
              src={imgExplodedDuct}
              alt="Exploded view of the cooktop duct subassembly"
            />
          }
        >
          <Beat title="General Dimension">
            <p>The hood fits perfectly into the standard size of a cooktop.</p>
          </Beat>
          <Beat>
            <p>
              The lifting module and the structure underneath do not take much
              space so the user can use lower part as a drawer for dishes or
              storage.
            </p>
          </Beat>
          <Specs
            items={[
              { label: "900 mm", body: "Cooktop width" },
              { label: "760 mm", body: "Duct height" },
              { label: "500 mm", body: "Cooktop depth" },
              { label: "200 mm", body: "Lifting travel" },
            ]}
            columns={2}
          />
        </Split>
      </Section>

      <Stage
        src={imgCooktop}
        alt="Cooktop interface with LED indication"
        title="Cooktop Interface"
        height="92dvh"
      >
        <p>
          Through the lights the LED inform the user about the smoke direction
          and most polluting area of the cooktop.
        </p>
      </Stage>

      <Section id="material" title="Material Selection">
        <Split
          media={<img src={imgMat1} alt="ABS plastic material sample" />}
          align="center"
        >
          <Beat title="ABS plastic">
            <p>
              High impact strength. High dimensional stability. Good electrical
              properties amongst others.
            </p>
          </Beat>
          <Specs items={ABS_NOTES} columns={1} />
        </Split>
      </Section>

      <Section
        id="moodboards"
        title="Form and Colour"
        lead="Moodboards set the language for the shell before any surfacing began."
      />

      <PanGallery label="Form and finish moodboard">
        <img src={imgMood1} alt="Form reference one" />
        <img src={imgMood2} alt="Form reference two" />
        <img src={imgMood3} alt="Form reference three" />
        <img src={imgMood4} alt="Form reference four" />
        <img src={imgMood5} alt="Form reference five" />
        <img src={imgMood6} alt="Form reference six" />
        <img src={imgFinP2} alt="Plastic finish reference" />
      </PanGallery>

      <Section id="colour" title="Colourways">
        <Grid min={220}>
          <Figure src={imgColor1} alt="Colourway one" />
          <Figure src={imgColor2} alt="Colourway two" />
          <Figure src={imgColor3} alt="Colourway three" />
          <Figure src={imgColor4} alt="Colourway four" />
        </Grid>
      </Section>

      <Section id="final" title="Final Concept">
        <Figure
          src={imgFinal}
          alt="Portable hood shown with its base and installed in a cooktop"
          bleed
          frame={false}
        />
        <Grid min={280}>
          <Figure src={imgFinS1} alt="Final concept, side study one" />
          <Figure src={imgFinS2} alt="Final concept, side study two" />
        </Grid>
      </Section>

      <Stage
        src={imgLifestyle2}
        alt="Portable hood carried through the apartment"
        title="Moved wherever the cooking happens."
        height="100dvh"
      />
    </CaseStudy>
  );
};

export default PortableHood;
